import { useIntl } from 'react-intl';
import {
  Form,
  Row,
  Col,
  FormInstance,
  Input,
  Checkbox,
  Typography,
} from 'antd';

import moment from 'moment';
import FloatingFormItem from '@/components/_common/FloatingFormItem';

import { useDefaultEmptyFieldError } from '@/hooks';
import { InputDate } from '@/components/_common';
import { getUserFromLocalStorage } from '@/utils/user';
import { useProfilesReduced } from '@/core/domain/enterprise/Profiles/Profiles.hooks';
import { useUsers } from '@/core/domain/enterprise/Users/Users.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useEffect } from 'react';
import { InformativeFormValues } from '@/core/domain/notification/Informative/Informative.types';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';

interface Props {
  form: FormInstance<InformativeFormValues>;
  isEdit?: boolean;
  usersId?: number[];
}

function InformativeForm({ form, isEdit, usersId }: Props) {
  const intl = useIntl();
  const { Title } = Typography;

  const listProfileIds = Form.useWatch('listProfileIds', form);

  const { employeeId } = getUserFromLocalStorage();

  const { data } = useProfilesReduced();
  const { data: usersData } = useUsers({ rows: 500 });

  const users = flatInfiniteData(usersData);

  useEffect(() => {
    if (employeeId) {
      form.setFieldsValue({ employeeID: Number(employeeId) });
    }
  }, [employeeId]);

  useEffect(() => {
    if (listProfileIds?.length) {
      if (isEdit) {
        form.setFieldsValue({ usersID: usersId });
      } else {
        form.setFieldsValue({
          usersID: users
            ?.filter((user) =>
              user.profiles.every((profile) =>
                listProfileIds?.includes(profile.id),
              ),
            )
            .map((user) => user.id),
        });
      }
    }
  }, [listProfileIds, usersData]);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const usersCreateList = users
    ?.filter((user) =>
      user.profiles.every((profile) => listProfileIds?.includes(profile.id)),
    )
    .map((user) => ({
      value: user.id,
      label: user.employeeName,
    }));

  const usersViewList = users
    ?.filter((user) => usersId?.includes(user.id))
    .map((user) => ({ value: user.id, label: user.employeeName }));

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[24, 24]}>
          <Col span={24} lg={12}>
            <Row gutter={[0, 9]}>
              <Col span={24}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'informative.form.date',
                  })}
                  name="dataInformation"
                  rules={[defaultEmptyFieldError]}
                  initialValue={moment()}
                >
                  <InputDate disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'informative.form.employee',
                  })}
                  name="employeeID"
                  rules={[defaultEmptyFieldError]}
                >
                  <EmployeesInfiniteSelectField
                    queryFilter={{ estimator: true }}
                    disabled
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'informative.form.mensage',
                  })}
                  name="message"
                  rules={[defaultEmptyFieldError]}
                >
                  <Input.TextArea rows={4} disabled={isEdit} maxLength={1000} />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          <Col span={24} lg={12}>
            <Row gutter={[24, 24]}>
              {!isEdit && (
                <Col span={24}>
                  <Form.Item
                    style={{
                      border: 'solid 1px #E2E2E2',
                      borderRadius: '0.5em',
                      padding: '1em',
                    }}
                    label={
                      <Title level={5}>
                        {intl.formatMessage({
                          id: 'informative.form.userGroup',
                        })}
                      </Title>
                    }
                    name="listProfileIds"
                  >
                    <Checkbox.Group
                      options={data?.map((profile) => ({
                        value: profile.id,
                        label: profile.name,
                      }))}
                      className="checkbox-user-groups"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={24}>
                <Form.Item
                  style={{
                    border: 'solid 1px #E2E2E2',
                    borderRadius: '0.5em',
                    padding: '1em',
                  }}
                  label={
                    <Title level={5}>
                      {intl.formatMessage({
                        id: 'informative.form.user',
                      })}
                    </Title>
                  }
                  name="usersID"
                >
                  <Checkbox.Group
                    options={isEdit ? usersViewList : usersCreateList}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                    disabled={isEdit}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default InformativeForm;
